// src/infra/api/axios.js
import axios          from 'axios';
import { getKeycloak } from '../tenant/keycloakSingleton';
import { clearAppSessionState } from '../utils/clearAppSessionState';

/* ------------------------------------------
 * util: pega o tenant da URL   /app_admin/…
 * ---------------------------------------- */
function currentTenant() {
    const [, maybe] = window.location.pathname.split('/');
    return maybe || null;
}

/*
 * Corrige strings que vieram com bytes UTF-8 interpretados como Latin-1.
 * Padrão dos erros: "ç" vira "Ã§", "ã" vira "Ã£", etc.
 *
 * Lógica:
 *   escape() recodifica cada caractere como %XX usando seu code-point como byte.
 *   Se a string estava errada (Latin-1), os pares %C3%A7, %C3%A3… são sequências
 *   UTF-8 válidas que decodeURIComponent reconstrói corretamente.
 *   Se a string já estava certa (UTF-8), escape() produz %E7 etc. que são bytes
 *   de continuação inválidos → decodeURIComponent lança → catch devolve original.
 */
function fixEncoding(value) {
    if (typeof value === 'string') {
        try {
            return decodeURIComponent(escape(value));
        } catch {
            return value;
        }
    }
    if (Array.isArray(value)) return value.map(fixEncoding);
    if (value !== null && typeof value === 'object') {
        const out = {};
        for (const key of Object.keys(value)) out[key] = fixEncoding(value[key]);
        return out;
    }
    return value;
}

/* ------------------------------------------
 * instância global
 * ---------------------------------------- */
const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL ?? 'http://localhost:8086/api',
    headers: { 'Content-Type': 'application/json' },
});

/* ------------------------------------------
 * REQUEST: Bearer + /{tenant}
 * ---------------------------------------- */
api.interceptors.request.use((cfg) => {
    const kc      = getKeycloak();
    const tenant  = currentTenant();

    if (kc?.token) {
        cfg.headers = { ...cfg.headers, Authorization: `Bearer ${kc.token}` };
    }

    if (tenant && cfg.url && !cfg.url.startsWith('http') && !cfg.url.startsWith(`/${tenant}`)) {
        const slash = cfg.url.startsWith('/') ? '' : '/';
        cfg.url = `/${tenant}${slash}${cfg.url}`;
    }

    return cfg;
});

/* ------------------------------------------
 * RESPONSE: corrige encoding + refresh-token
 * ---------------------------------------- */
api.interceptors.response.use(
    (res) => {
        if (res.data != null && res.config?.responseType !== 'blob') {
            res.data = fixEncoding(res.data);
        }
        return res;
    },
    async (err) => {
        const kc     = getKeycloak();
        const status = err.response?.status;

        // Só 401 (token inválido/expirado) tenta refresh e, se falhar, desloga.
        // 403 é falta de PERMISSÃO (token válido) — refresh não resolve e NÃO deve
        // deslogar; deixa o erro subir pra tela tratar/exibir "sem permissão".
        if (kc && status === 401) {
            try {
                const refreshed = await kc.updateToken(5);
                if (refreshed) {
                    const cfg = err.config;
                    cfg.headers.Authorization = `Bearer ${kc.token}`;
                    return api(cfg);
                }
            } catch { /* refresh falhou */ }

            clearAppSessionState();
            kc.logout({ redirectUri: window.location.origin });
        }

        return Promise.reject(err);
    }
);

export default api;
